// client/src/components/marketer/project/modals/ProjectDetailModal.js
import React, { useState, useEffect, useMemo } from 'react';
import ProjectJourneyBox from '../../../common/ProjectJourneyBox';
import CommonGuideBox from '../../../common/CommonGuideBox';
import '../../../../styles/marketer/modals/ProjectPreviewModal.css';
import { linkifyHtml } from '../../../../utils/linkify';

const API_URL = process.env.REACT_APP_API_URL || '';

function toDateStr(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
}

export default function ProjectDetailModal({ open, onClose, project, projectId }) {
  const [fullProject, setFullProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);

  // API로 전체 프로젝트 정보 조회
  useEffect(() => {
    if (!open) return;
    
    // ✅ project prop이 이미 있으면 사용 (API 호출 불필요)
    if (project) {
      // project 데이터를 fullProject 형식으로 변환
      const transformedProject = {
        projectNumber: project.projectNumber,
        serviceType: project.serviceType,
        product: {
          name: project.productName || project.product?.name,
          placeName: project.product?.placeName,
          customTitle: project.product?.customTitle,
          price: project.product?.price,
          url: project.product?.url,
          purchasePlace: project.product?.purchasePlace,
          placeAddress: project.product?.placeAddress,
          customDesc: project.product?.customDesc
        },
        images: {
          cover: project.coverImage || project.cover || project.images?.cover,
          gallery: project.gallery || project.images?.gallery || []
        },
        recruit: project.recruit || {
          rewardPointPerCreator: project.rewardPointPerCreator,
          headcount: project.headcount,
          contentType: project.contentType,
          platforms: project.platforms
        },
        perPurchaseReward: project.perPurchaseReward,
        schedule: {
          recruitEnd: project.recruitEnd || project.schedule?.recruitEnd,
          submitEnd: project.submitEnd || project.schedule?.submitEnd,
          settlementDates: project.perPurchaseReward?.settlementDates || project.schedule?.settlementDates || []
        },
        description: project.description || '상세 가이드 내용이 등록되지 않았습니다.',
        manager: project.manager || {}
      };
      
      setFullProject(transformedProject);
      setLoading(false);
      return;
    }
    
    // ✅ projectId만 있는 경우 API 호출 시도
    if (!projectId) return;

    const fetchFullProject = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const id = projectId;
        const res = await fetch(`${API_URL}/api/marketer/mybiz/projects/${id}`, {
          credentials: 'include'
        });
        
        const data = await res.json();
        
        if (!res.ok || !data.ok) {
          throw new Error(data.message || '프로젝트 정보 조회 실패');
        }
        
        // ✅ API 응답에서 cover, gallery가 직접 있으면 images 구조로 변환
        const apiProject = data.project;
        setFullProject({
          ...apiProject,
          images: {
            cover: apiProject.cover || apiProject.images?.cover,
            gallery: apiProject.gallery || apiProject.images?.gallery || []
          }
        });
      } catch (err) {
        console.error('프로젝트 상세 조회 에러:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFullProject();
  }, [open, project, projectId]);

  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
    }
  }, [open, fullProject]); // fullProject가 변경될 때도 리셋

  // 전체 이미지 배열을 useMemo로 메모이제이션
  const allImages = useMemo(() => {
    if (!fullProject) return [];
    return [
      fullProject.images?.cover,
      ...(fullProject.images?.gallery || [])
    ].filter(Boolean);
  }, [fullProject]);

  // 이미지 프리로딩
  useEffect(() => {
    if (allImages.length === 0) return;
    
    // 모든 이미지를 백그라운드에서 프리로드
    allImages.forEach((url) => {
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [allImages]);

  // 이미지 로딩 감지
  useEffect(() => {
    if (allImages.length === 0 || currentImageIndex >= allImages.length) return;
    
    setImageLoading(true);
    const img = new Image();
    img.src = allImages[currentImageIndex];
    img.onload = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
  }, [currentImageIndex, allImages]);

  // 모달이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (open) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll'; // 스크롤바 자리 유지
      
      return () => {
        // 모달이 닫힐 때 원래대로 복원
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const handlePrevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (imageLoading) return; // 로딩 중에는 클릭 무시
    const newIndex = currentImageIndex === 0 ? allImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
  };

  const handleNextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (imageLoading) return; // 로딩 중에는 클릭 무시
    const newIndex = currentImageIndex === allImages.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
  };

  if (!open) return null;

  // 로딩 상태
  if (loading) {
    return (
      <div className="project-preview-modal mbm-overlay">
        <div className="project-preview-modal__content mbm" style={{ maxWidth: 400, padding: 40 }}>
          <div style={{ textAlign: 'center', color: 'var(--mb-text-sub)' }}>
            <div style={{ fontSize: 16 }}>프로젝트 정보를 불러오는 중...</div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="project-preview-modal mbm-overlay">
        <div className="project-preview-modal__content mbm" style={{ maxWidth: 400, padding: 40 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--mb-color-danger)', marginBottom: 16, fontSize: 16 }}>
              ⚠️ {error}
            </div>
            <button onClick={onClose} className="mb-btn mb-btn-secondary">
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!fullProject) return null;

  // 제품명 추출
  const getProductName = () => {
    if (fullProject.serviceType === 'delivery' || fullProject.serviceType === 'purchase') {
      return fullProject.product?.name || '광고 대상';
    } else if (fullProject.serviceType === 'visit') {
      return fullProject.product?.placeName || '광고 대상';
    } else if (fullProject.serviceType === 'free') {
      return fullProject.product?.customTitle || '광고 대상';
    }
    return '광고 대상';
  };

  // 광고 형식 추출
  const getServiceTypeLabel = () => {
    if (fullProject.serviceType === 'delivery') return '제품 제공';
    if (fullProject.serviceType === 'purchase') return '제품 미제공';
    if (fullProject.serviceType === 'visit') return '장소 방문';
    if (fullProject.serviceType === 'free') return '자율';
    return '';
  };

  // 서비스 타입별 안내 문구
  const getServiceTypeDescription = () => {
    if (fullProject.serviceType === 'delivery') {
      return '해당 프로젝트는 광고주가 리뷰할 제품을 제공해주는 프로젝트 입니다. 프로필에 설정된 주소로 제품이 배송됩니다.';
    }
    if (fullProject.serviceType === 'purchase') {
      return '해당 프로젝트는 인플루언서가 리뷰할 제품을 직접 구매해야하는 프로젝트입니다, 제품은 제공되지 않습니다.';
    }
    if (fullProject.serviceType === 'visit') {
      return '해당 프로젝트는 장소/서비스를 홍보하는 프로젝트 입니다.';
    }
    if (fullProject.serviceType === 'free') {
      return '해당 프로젝트는 자율형식으로 진행됩니다. 광고주가 제시하는 상세 가이드를 잘 확인하세요';
    }
    return '';
  };

  return (
    <div className="project-preview-modal mbm-overlay">
      <div className="project-preview-modal__content mbm" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="project-preview-modal__header-bar mbm-head">
          <div className="project-preview-modal__header-title mbm-title">
            <span>프로젝트 상세보기</span>
          </div>
          <button onClick={onClose} aria-label="close" className="project-preview-modal__close mbm-close">×</button>
        </div>

        <div className="project-preview-modal__body">
          
          {/* 프로젝트 소개 문구 */}
          <div className="project-preview-modal__intro">
            <strong>{getProductName()}</strong>을(를) 홍보하는 <strong>{getServiceTypeLabel()}</strong> 프로젝트입니다.
          </div>

          {/* 서비스 타입별 안내 */}
          <div className="project-preview-modal__service-notice">
            {getServiceTypeDescription()}
          </div>

          {/* 프로젝트 진행 순서 */}
          <ProjectJourneyBox />

          {/* 공통 가이드 */}
          <CommonGuideBox />
          
          {/* 섹션 1: 프로젝트 번호 */}
          <div style={{ 
            background: '#f0f5ff', 
            padding: '16px 20px', 
            borderRadius: 12,
            marginBottom: 24,
            border: '1px solid #d6e4ff'
          }}>
            <div style={{ 
              fontSize: 13, 
              color: 'var(--mb-color-primary)', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 4
            }}>
              Project Number
            </div>
            <div style={{ 
              fontSize: 20, 
              fontWeight: 700, 
              color: 'var(--mb-text-title)',
              fontFamily: 'monospace'
            }}>
              {fullProject.projectNumber || 'N/A'}
            </div>
          </div>

          {/* 섹션 2: 상세 정보 그리드 */}
          <div className="project-preview-modal__main-grid">
            
            {/* 왼쪽 컬럼: 프로젝트 및 미션 설명 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* 프로젝트 기본 정보 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title-box">
                  <div className="project-preview-modal__card-title">📦 프로젝트 대상 정보</div>
                </div>

                {/* 이미지 슬라이더 */}
                {allImages.length > 0 && (
                  <div className="project-preview-modal__image-slider">
                    <div className="project-preview-modal__image-container">
                      <img 
                        src={allImages[currentImageIndex]} 
                        alt={`프로젝트 이미지 ${currentImageIndex + 1}`}
                        className="project-preview-modal__slider-image"
                        style={{ 
                          opacity: imageLoading ? 0.5 : 1,
                          transition: 'opacity 0.2s ease-in-out'
                        }}
                      />
                      {allImages.length > 1 && (
                        <>
                          <button 
                            className="project-preview-modal__slider-btn project-preview-modal__slider-btn--prev"
                            onClick={handlePrevImage}
                            disabled={imageLoading}
                            aria-label="이전 이미지"
                            type="button"
                          >
                            ‹
                          </button>
                          <button 
                            className="project-preview-modal__slider-btn project-preview-modal__slider-btn--next"
                            onClick={handleNextImage}
                            disabled={imageLoading}
                            aria-label="다음 이미지"
                            type="button"
                          >
                            ›
                          </button>
                        </>
                      )}
                    </div>
                    {allImages.length > 1 && (
                      <div className="project-preview-modal__slider-indicator">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    )}
                  </div>
                )}

                <div className="project-preview-modal__info-list">
                  {fullProject.serviceType === 'delivery' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🎁</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">광고할 제품</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 판매처의 제품 가격</div>
                          <div className="project-preview-modal__info-value">{Number(fullProject.product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 참고 URL</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#2f6bff', wordBreak: 'break-all' }}>{fullProject.product?.url || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {fullProject.serviceType === 'purchase' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🛒</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">리뷰할 제품</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 가격</div>
                          <div className="project-preview-modal__info-value">{Number(fullProject.product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 (구매)참고 URL</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#2f6bff', wordBreak: 'break-all' }}>{fullProject.product?.purchasePlace || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {fullProject.serviceType === 'visit' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏛️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">장소/서비스 명</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.placeName || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🗺️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">주소 or URL</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.placeAddress || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {fullProject.serviceType === 'free' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">📝</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제목</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.customTitle || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">ℹ️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">참고 설명</div>
                          <div className="project-preview-modal__info-value">{fullProject.product?.customDesc || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 미션 설명 및 가이드 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title project-preview-modal__card-title--large">⚠️ 광고주 요청 사항 (중요)</div>
                <div className="project-preview-modal__info-box-blue">
                  <div className="project-preview-modal__info-box-title">💡 선정된 인플루언서가 수행할 활동입니다</div>
                  <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.5 }}>
                    아래 광고주 요청사항에 맞춰 콘텐츠를 제작하고 업로드해주세요.
                  </div>
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  lineHeight: 1.8, 
                  color: '#334155',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '16px'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: linkifyHtml(fullProject.description) || '<span style="color:#9ca3af">상세 가이드 내용이 등록되지 않았습니다.</span>' }} />
                </div>
              </div>
            </div>

            {/* 오른쪽 컬럼: 리워드, 일정, 담당자 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* 리워드 혜택 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title">💰 참여 리워드</div>
                <div className="project-preview-modal__reward-large">
                  <div style={{ fontSize: '14px', fontWeight: 700, opacity: 0.9 }}>기본 리워드</div>
                  <div className="project-preview-modal__reward-amount">{Number(fullProject.recruit?.rewardPointPerCreator||0).toLocaleString()} pt</div>
                  <div style={{ fontSize: '13px', opacity: 0.8 }}>콘텐츠 최종 승인 시 즉시 지급</div>
                </div>
                {fullProject.perPurchaseReward?.use && (
                  <>
                    <div className="project-preview-modal__info-box-blue" style={{ background: '#f0fdf4', borderColor: '#dcfce7' }}>
                      <div className="project-preview-modal__info-box-title" style={{ color: '#166534' }}>📈 성과형 리워드 (CPS)</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#15803d' }}>
                        구매 고객 1명당 {Number(fullProject.perPurchaseReward?.rewardPerBuyer||0).toLocaleString()} pt
                        {fullProject.perPurchaseReward?.budgetTotal && fullProject.perPurchaseReward?.settlementCount && fullProject.recruit?.headcount && (
                          <span> / 회차당 최대 {Math.floor(Number(fullProject.perPurchaseReward.budgetTotal) / Number(fullProject.recruit.headcount) / Number(fullProject.perPurchaseReward.settlementCount)).toLocaleString()} pt</span>
                        )}
                        {fullProject.perPurchaseReward?.settlementCount && (
                          <span> / 총 {fullProject.perPurchaseReward.settlementCount}회 결산</span>
                        )}
                      </div>
                    </div>
                    <div className="project-preview-modal__cps-info">
                      <div className="project-preview-modal__cps-info-title">💡 성과형 리워드란?</div>
                      <div className="project-preview-modal__cps-info-text">
                        계약 시, 광고주가 인플루언서에게 URL을 제공합니다. 해당 URL을 통해 고객이 제품을 구매하게 될 시, 인플루언서에게 광고비가 추가로 지급됩니다.
                      </div>
                    </div>
                  </>
                )}
                <div style={{ textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                  현재 <b>{Number(fullProject.recruit?.headcount||0)}명</b>의 인플루언서를 찾고 있습니다.
                </div>
              </div>

              {/* 모집 조건 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title">📋 모집 조건</div>
                
                {/* 모집하는 SNS 플랫폼 */}
                {fullProject.recruit?.platforms && fullProject.recruit.platforms.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--mb-text-title)', marginBottom: 10 }}>
                      모집하는 SNS 채널
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {fullProject.recruit.platforms.map((platform, idx) => {
                        const platformLabels = {
                          instagram: '인스타그램',
                          youtube: '유튜브',
                          blog: '블로그',
                          tiktok: '틱톡'
                        };
                        const platformIcons = {
                          instagram: '/images/sns/instagram.svg',
                          youtube: '/images/sns/youtube.svg',
                          blog: '/images/sns/blog.svg',
                          tiktok: '/images/sns/tiktok.svg'
                        };
                        return (
                          <div 
                            key={idx} 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              padding: '10px 16px',
                              background: '#f6f8fb',
                              border: '1px solid var(--mb-border-color)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'var(--mb-text-body)'
                            }}
                          >
                            <img 
                              src={platformIcons[platform]} 
                              alt={platform}
                              style={{ width: 18, height: 18, objectFit: 'contain' }}
                            />
                            <span>{platformLabels[platform] || platform}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* 콘텐츠 형식 */}
                {fullProject.recruit?.contentType && (
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--mb-text-title)', marginBottom: 10 }}>
                      광고 콘텐츠 형식
                    </div>
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      padding: '10px 18px',
                      background: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: '#92400e'
                    }}>
                      {fullProject.recruit.contentType === 'post' && '📝 글/사진 포스팅'}
                      {fullProject.recruit.contentType === 'video' && '🎥 동영상 콘텐츠'}
                      {fullProject.recruit.contentType === 'free' && '✨ 자율형식'}
                    </div>
                  </div>
                )}
              </div>

              {/* 프로젝트 일정 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title">📅 주요 일정 안내</div>
                <div className="project-preview-modal__info-list">
                  <div className="project-preview-modal__info-item">
                    <div className="project-preview-modal__info-icon-sm">⏳</div>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">모집 및 선정 마감</div>
                      <div className="project-preview-modal__info-value">{toDateStr(fullProject.schedule?.recruitEnd)} 까지</div>
                      <div className="project-preview-modal__info-subtext">선정 시 안내 문자가 발송됩니다.</div>
                    </div>
                  </div>
                  <div className="project-preview-modal__info-item">
                    <div className="project-preview-modal__info-icon-sm">📤</div>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">콘텐츠 제출 마감</div>
                      <div className="project-preview-modal__info-value">{toDateStr(fullProject.schedule?.submitEnd)} 까지</div>
                      <div className="project-preview-modal__info-subtext">반드시 기한 내에 업로드 해주세요.</div>
                    </div>
                  </div>
                  {fullProject.perPurchaseReward?.use && fullProject.schedule?.settlementDates?.length > 0 && (
                    <div className="project-preview-modal__info-item">
                      <div className="project-preview-modal__info-icon-sm">💰</div>
                      <div className="project-preview-modal__info-text-box">
                        <div className="project-preview-modal__info-label">CPS 결산일</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {fullProject.schedule.settlementDates.map((date, idx) => (
                            <div key={idx} className="project-preview-modal__info-value" style={{ fontSize: '14px' }}>
                              {idx + 1}회차: {toDateStr(date)}
                            </div>
                          ))}
                        </div>
                        <div className="project-preview-modal__info-subtext">결산일에 성과형 리워드가 정산됩니다.</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 담당자 연락처 */}
              {fullProject.manager && (fullProject.manager.phone || fullProject.manager.email || fullProject.manager.officeHour) && (
                <div className="project-preview-modal__card" style={{ background: '#f8fafc' }}>
                  <div className="project-preview-modal__card-title">📞 프로젝트 지원 센터</div>
                  <div className="project-preview-modal__info-list">
                    {fullProject.manager.phone && (
                      <div className="project-preview-modal__info-item" style={{ background: 'white' }}>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">문의 연락처</div>
                          <div className="project-preview-modal__info-value">{fullProject.manager.phone}</div>
                        </div>
                      </div>
                    )}
                    {fullProject.manager.email && (
                      <div className="project-preview-modal__info-item" style={{ background: 'white' }}>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">문의 이메일</div>
                          <div className="project-preview-modal__info-value">{fullProject.manager.email}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {fullProject.manager.officeHour && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
                      응답 가능 시간: {fullProject.manager.officeHour}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}